import { Navigate, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { useAuth } from "context/AuthContext";
import { createPaymentSession } from "layouts/dashboardLayout/pages/Users/services/graphqlService";
import { isAirportEntityType } from "utils/tokenUtils";

const FREE_FEATURES = [
  "Limited Resource Requests",
  "Limited Missions",
];

const PAID_FEATURES = [
  "Dashboards KPIs",
  "Tasks",
  "Resources Requests",
  "Mission Lifecycle",
];

function FeatureRow({ label, included, emphasize }) {
  return (
    <SoftBox display="flex" alignItems="center" py={0.85}>
      <SoftBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        width="1.5rem"
        height="1.5rem"
        borderRadius="50%"
        bgColor={included ? "success" : "secondary"}
        variant="gradient"
        mr={1.5}
        sx={{ flexShrink: 0 }}
      >
        <Icon sx={{ fontSize: "0.9rem !important", color: "#fff", fontWeight: "bold" }}>
          {included ? "done" : "remove"}
        </Icon>
      </SoftBox>
      <SoftTypography
        variant="button"
        color={emphasize ? "white" : "text"}
        fontWeight={included ? "medium" : "regular"}
        sx={{ opacity: included ? 1 : 0.55 }}
      >
        {label}
      </SoftTypography>
    </SoftBox>
  );
}

function PricingPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const { tokenEntityType, isGuest, isLoading, user } = auth;
  const [isSubscribing, setIsSubscribing] = useState(false);

  const notify = ({ type, title, message }) => {
    window.dispatchEvent(
      new CustomEvent("globalNotification", {
        detail: { type, title, message },
      })
    );
  };

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("checkout") !== "cancelled") {
      return;
    }
    window.dispatchEvent(
      new CustomEvent("globalNotification", {
        detail: {
          type: "warning",
          title: "Checkout cancelled",
          message: "Your subscription was not completed.",
        },
      })
    );
    window.history.replaceState(null, "", "/dashboard/pricing");
  }, []);

  if (isLoading) return null;

  if (isGuest || !isAirportEntityType(tokenEntityType)) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleContinueFree = () => {
    navigate("/dashboard/analytics", { replace: true });
  };

  const handleSubscribe = async () => {
    if (!user?.id_token) {
      notify({
        type: "error",
        title: "Missing billing context",
        message: "Please refresh and try again after your profile loads.",
      });
      return;
    }

    try {
      setIsSubscribing(true);
      const result = await createPaymentSession({
        successUrl: `${window.location.origin}/dashboard/payments?checkout=success`,
        cancelUrl: `${window.location.origin}/dashboard/pricing?checkout=cancelled`,
      }, user.id_token);

      if (!result?.data?.url) {
        throw new Error("Stripe Checkout URL was not returned.");
      }

      window.location.href = result.data.url;
    } catch (error) {
      notify({
        type: "error",
        title: "Checkout unavailable",
        message: error.message || "Could not start Stripe checkout.",
      });
      setIsSubscribing(false);
    }
  };

  return (
    <SoftBox py={1}>
      <SoftBox
        mb={4}
        textAlign="center"
        px={{ xs: 1, md: 4 }}
        sx={{
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: { xs: "0 -1rem", md: "0 -2rem" },
            height: "100%",
            background:
              "radial-gradient(ellipse 70% 80% at 50% 0%, rgba(0, 32, 84, 0.08), transparent 70%)",
            pointerEvents: "none",
            zIndex: 0,
          },
        }}
      >
        <SoftBox position="relative" zIndex={1} pt={1} pb={1}>
          <SoftBadge
            variant="contained"
            color="primary"
            size="sm"
            badgeContent="Airport plans"
            container
            circular
          />
          <SoftTypography
            variant="h3"
            fontWeight="bold"
            color="dark"
            mt={2}
            mb={1}
          >
            Choose your airport plan
          </SoftTypography>
          <SoftTypography
            variant="body2"
            color="text"
            sx={{ maxWidth: 520, mx: "auto" }}
          >
            Start free with limited access, or unlock the full airport toolkit
            with dashboards, tasks, and complete mission lifecycle management.
          </SoftTypography>
        </SoftBox>
      </SoftBox>

      <Grid container spacing={3} justifyContent="center" alignItems="stretch">
        <Grid item xs={12} sm={10} md={5} lg={4}>
          <Card
            sx={{
              height: "100%",
              border: "1px solid",
              borderColor: "grey.200",
              boxShadow: "0 8px 24px rgba(0, 32, 84, 0.06)",
              overflow: "hidden",
            }}
          >
            <SoftBox p={3} display="flex" flexDirection="column" height="100%">
              <SoftBadge
                variant="contained"
                color="secondary"
                size="sm"
                badgeContent="Free tier"
                container
                circular
              />
              <SoftBox mt={2} mb={0.5}>
                <SoftTypography variant="h2" fontWeight="bold" color="dark">
                  <SoftTypography
                    component="span"
                    variant="h4"
                    color="text"
                    fontWeight="medium"
                    verticalAlign="top"
                  >
                    $
                  </SoftTypography>
                  0
                </SoftTypography>
                <SoftTypography variant="caption" color="text" display="block">
                  forever free
                </SoftTypography>
              </SoftBox>
              <SoftTypography variant="body2" color="text" mb={2.5}>
                Essential access to get started with limited capacity.
              </SoftTypography>

              <SoftBox flexGrow={1} mb={3}>
                {FREE_FEATURES.map((label) => (
                  <FeatureRow key={label} label={label} included />
                ))}
                {PAID_FEATURES.map((label) => (
                  <FeatureRow key={label} label={label} included={false} />
                ))}
              </SoftBox>

              <SoftButton
                variant="outlined"
                color="dark"
                fullWidth
                onClick={handleContinueFree}
              >
                Continue free
              </SoftButton>
            </SoftBox>
          </Card>
        </Grid>

        <Grid item xs={12} sm={10} md={5} lg={4}>
          <Card
            sx={{
              height: "100%",
              background: "linear-gradient(165deg, #002054 0%, #012c73 55%, #0a3d8f 100%)",
              boxShadow: "0 16px 40px rgba(0, 32, 84, 0.28)",
              overflow: "hidden",
              position: "relative",
              transform: { md: "scale(1.02)" },
            }}
          >
            <SoftBox
              position="absolute"
              top={0}
              right={0}
              width="180px"
              height="180px"
              sx={{
                background:
                  "radial-gradient(circle at top right, rgba(23, 193, 232, 0.35), transparent 65%)",
                pointerEvents: "none",
              }}
            />
            <SoftBox p={3} display="flex" flexDirection="column" height="100%" position="relative">
              <SoftBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                <SoftBadge
                  variant="contained"
                  color="info"
                  size="sm"
                  badgeContent="Paid subscription"
                  container
                  circular
                />
                <SoftTypography
                  variant="caption"
                  fontWeight="bold"
                  color="white"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.12)",
                    px: 1.25,
                    py: 0.5,
                    borderRadius: "0.5rem",
                    letterSpacing: "0.02em",
                  }}
                >
                  Recommended
                </SoftTypography>
              </SoftBox>

              <SoftBox mt={2} mb={0.5}>
                <SoftTypography variant="h2" fontWeight="bold" color="white">
                  <SoftTypography
                    component="span"
                    variant="h4"
                    color="white"
                    fontWeight="medium"
                    verticalAlign="top"
                    sx={{ opacity: 0.85 }}
                  >
                    $
                  </SoftTypography>
                  499
                </SoftTypography>
                <SoftTypography
                  variant="caption"
                  color="white"
                  display="block"
                  sx={{ opacity: 0.75 }}
                >
                  per airport
                </SoftTypography>
              </SoftBox>
              <SoftTypography
                variant="body2"
                color="white"
                mb={2.5}
                sx={{ opacity: 0.85 }}
              >
                Full operational visibility across requests, tasks, and missions.
              </SoftTypography>

              <SoftBox flexGrow={1} mb={3}>
                {FREE_FEATURES.map((label) => (
                  <FeatureRow key={label} label={label} included emphasize />
                ))}
                {PAID_FEATURES.map((label) => (
                  <FeatureRow key={label} label={label} included emphasize />
                ))}
              </SoftBox>

              <SoftButton
                variant="gradient"
                color="info"
                fullWidth
                onClick={handleSubscribe}
                disabled={isSubscribing}
              >
                {isSubscribing ? "Starting checkout..." : "Subscribe"}&nbsp;
                <Icon sx={{ fontWeight: "bold" }}>arrow_forward</Icon>
              </SoftButton>
            </SoftBox>
          </Card>
        </Grid>
      </Grid>
    </SoftBox>
  );
}

export default PricingPage;
